import type { RunToDatabase } from '@/types'
import { CorruptedDatabaseFileException } from '@pkg/basic'
import {
  type DatabaseWithTransaction,
  type PortfolioMigration,
  type TransactionDatabase,
  portfolioMigrations,
} from '@pkg/database-portfolio'
import { sql } from 'drizzle-orm'
import { z } from 'zod'
import { detectLegacyMigrationId } from './legacyMigration'
import { repairForeignKeyViolations } from './repairForeignKeyViolations'
import { reportForeignKeyRepair } from './reportForeignKeyRepair'

/** stable migration keyを保存する管理table名。 */
const MIGRATION_TABLE_NAME = '__portfolioMigrations'

/** 旧browser runnerが使用していた管理table名。 */
const LEGACY_MIGRATION_TABLE_NAME = '__migrations'

/** 旧browser migration IDのquery結果を検証するschema。 */
const legacyMigrationRowsSchema = z.array(z.tuple([z.number().int()]))

/** stable migration履歴のquery結果を検証するschema。 */
const migrationRowsSchema = z.array(z.tuple([z.string(), z.string()]))

/**
 * raw SQLをbrowser DB transactionの1 stepへ変換する。
 * @param params - statement: transaction内で実行するSQL。
 * @returns SQL実行Promiseを返すtransaction step。
 */
const createMigrationTransactionStep =
  ({
    statement,
  }: {
    readonly statement: ReturnType<typeof sql.raw>
  }) =>
  (transactionDatabase: TransactionDatabase): Promise<unknown>[] => [
    Promise.resolve(transactionDatabase.run(statement)),
  ]

/**
 * foreign_keys PRAGMAの切り替えstatementか確認する。
 * @param params - statement: 確認対象のraw SQL。
 * @returns foreign_keysをONまたはOFFへ切り替えるstatementの場合はtrue。
 */
const isForeignKeysToggleStatement = ({
  statement,
}: {
  readonly statement: string
}): boolean => /^PRAGMA\s+FOREIGN_KEYS\s*=\s*(?:ON|OFF)\s*;?$/i.test(statement)

/**
 * `PRAGMA foreign_key_check` の1行 `(table, rowid, parent, fkid)` を検証するschema。
 * 集計に使うのは違反row側のtable名と参照先table名だけのため、他列はunknownで受ける。
 */
const foreignKeyCheckRowSchema = z.tuple([
  z.string(),
  z.unknown(),
  z.string(),
  z.unknown(),
])

/** relation単位に集計した外部キー違反件数。keyは`child -> parent`。 */
type ForeignKeyViolationCounts = ReadonlyMap<string, number>

/**
 * foreign_key_checkの結果をrelation単位の件数へ集計する。
 * rowidはtable再構築で振り直されるため、比較には件数だけを使う。
 * @param params - rows: foreign_key_checkが返した行。
 * @returns relation keyごとの違反件数。
 */
const countForeignKeyViolations = ({
  rows,
}: {
  readonly rows: readonly unknown[]
}): ForeignKeyViolationCounts => {
  const counts = new Map<string, number>()
  for (const row of rows) {
    const parsedRow = foreignKeyCheckRowSchema.safeParse(row)
    const relation = parsedRow.success
      ? `${parsedRow.data[0]} -> ${parsedRow.data[2]}`
      : 'unparsable row'
    counts.set(relation, (counts.get(relation) ?? 0) + 1)
  }
  return counts
}

/**
 * migration前後の違反件数を比較し、増加したrelationだけを抽出する。
 * 既存の無関係な違反でmigration chainを止めないための判定に使う。
 * @param params - baseline: migration前の件数、current: migration後の件数。
 * @returns 増加したrelationの要約。増加がなければ空配列。
 */
export const detectForeignKeyRegressions = ({
  baseline,
  current,
}: {
  readonly baseline: ForeignKeyViolationCounts
  readonly current: ForeignKeyViolationCounts
}): readonly string[] => {
  const regressions: string[] = []
  for (const [relation, currentCount] of current) {
    const baselineCount = baseline.get(relation) ?? 0
    if (currentCount > baselineCount) {
      regressions.push(`${relation} ${baselineCount} -> ${currentCount}`)
    }
  }
  return regressions.sort()
}

/**
 * DB全体の外部キー違反をrelation単位で集計する。
 * @param params - database: 接続先。
 * @returns relation keyごとの違反件数。
 */
const readForeignKeyViolationCounts = async ({
  database,
}: {
  readonly database: DatabaseWithTransaction['database']
}): Promise<ForeignKeyViolationCounts> =>
  countForeignKeyViolations({
    rows: await database.values(sql`PRAGMA foreign_key_check;`),
  })

/**
 * migrationが新たに外部キー違反を増やしていないか検証するstepを生成する。
 * foreign_key_checkはDB全体を検査するため、絶対数ではなくmigration前との差分で判定する。
 * これによりmigration自身のバグだけを検出し、既存の違反ではchainを止めない。
 * @param params - migration: 検証対象migration、baseline: migration前の違反件数。
 * @returns 違反が増加した場合に例外を返すtransaction step。
 */
const createForeignKeyRegressionCheckTransactionStep =
  ({
    migration,
    baseline,
  }: {
    readonly migration: PortfolioMigration
    readonly baseline: ForeignKeyViolationCounts
  }) =>
  (transactionDatabase: TransactionDatabase): Promise<unknown>[] => [
    Promise.resolve(
      transactionDatabase.values(sql`PRAGMA foreign_key_check;`),
    ).then((foreignKeyErrors) => {
      const regressions = detectForeignKeyRegressions({
        baseline,
        current: countForeignKeyViolations({ rows: foreignKeyErrors }),
      })
      if (regressions.length > 0) {
        throw new CorruptedDatabaseFileException(
          `foreign key check failed after migration: ${migration.key} [${regressions.join(', ')}]`,
        )
      }
    }),
  ]

/**
 * sqlite_masterにtableが存在するか確認する。
 * @param params - database: 接続先、tableName: 確認対象table名。
 * @returns tableが存在する場合はtrue。
 */
const tableExists = async ({
  database,
  tableName,
}: {
  readonly database: DatabaseWithTransaction['database']
  readonly tableName: string
}): Promise<boolean> => {
  const rows = await database.values(
    sql`SELECT 1 FROM sqlite_master WHERE type = 'table' AND name = ${tableName} LIMIT 1;`,
  )
  return rows.length > 0
}

/**
 * migration適用履歴を追加するSQLを生成する。
 * @param params - migration: 適用済みとして保存するmigration。
 * @returns migration履歴を追加するSQL。
 */
const createInsertMigrationStatement = ({
  migration,
}: {
  readonly migration: PortfolioMigration
}): ReturnType<typeof sql> => sql`
  INSERT INTO ${sql.raw(`\`${MIGRATION_TABLE_NAME}\``)}
    (\`key\`, \`checksum\`, \`appliedAt\`)
  VALUES (${migration.key}, ${migration.checksum}, ${Date.now()});
`

/**
 * 旧browser migration IDをcanonical migration一覧へ変換する。
 * @param params - legacyMigrationId: 旧runnerが記録または復元した最終ID。
 * @returns 適用済みとみなすcanonical migration一覧。
 */
const mapLegacyMigrationId = ({
  legacyMigrationId,
}: {
  readonly legacyMigrationId: number
}): readonly PortfolioMigration[] =>
  portfolioMigrations.filter((_migration, index) => {
    if (index <= 13) {
      return index <= legacyMigrationId
    }
    if (index === 14) {
      return legacyMigrationId >= 15
    }
    if (index === 15) {
      return legacyMigrationId >= 16
    }
    if (index === 16) {
      return legacyMigrationId >= 14
    }
    return false
  })

/**
 * 旧管理tableまたはschema signatureから旧browser migration IDを取得する。
 * @param params - database: 接続先、isInitialMigration: 新規DB作成中かどうか。
 * @returns 旧migration ID。新規DBにはundefinedを返す。
 */
const getLegacyMigrationId = async ({
  database,
  isInitialMigration,
}: {
  readonly database: DatabaseWithTransaction['database']
  readonly isInitialMigration: boolean
}): Promise<number | undefined> => {
  if (
    await tableExists({
      database,
      tableName: LEGACY_MIGRATION_TABLE_NAME,
    })
  ) {
    const rows = legacyMigrationRowsSchema.parse(
      await database.values(
        sql`SELECT \`id\` FROM ${sql.raw(`\`${LEGACY_MIGRATION_TABLE_NAME}\``)} ORDER BY \`id\` DESC LIMIT 1;`,
      ),
    )
    return rows[0]?.[0] ?? -1
  }
  if (isInitialMigration) {
    return undefined
  }
  return await detectLegacyMigrationId({ database })
}

/**
 * stable key形式のmigration管理tableを作成し、旧履歴を変換する。
 * @param params - database: 接続先、isInitialMigration: 新規DB作成中かどうか。
 * @returns 初期化完了時に解決するPromise。
 */
const initializeMigrationTable = async ({
  database,
  isInitialMigration,
}: {
  readonly database: DatabaseWithTransaction
  readonly isInitialMigration: boolean
}): Promise<void> => {
  if (
    await tableExists({
      database: database.database,
      tableName: MIGRATION_TABLE_NAME,
    })
  ) {
    return
  }

  const legacyMigrationId = await getLegacyMigrationId({
    database: database.database,
    isInitialMigration,
  })
  const appliedMigrations =
    legacyMigrationId === undefined
      ? []
      : mapLegacyMigrationId({ legacyMigrationId })
  const createTableStatement = sql.raw(`
    CREATE TABLE \`${MIGRATION_TABLE_NAME}\` (
      \`key\` TEXT PRIMARY KEY NOT NULL,
      \`checksum\` TEXT NOT NULL,
      \`appliedAt\` INTEGER NOT NULL
    );
  `)

  await database.transaction([
    createMigrationTransactionStep({ statement: createTableStatement }),
    ...appliedMigrations.map((migration) =>
      createMigrationTransactionStep({
        statement: createInsertMigrationStatement({ migration }),
      }),
    ),
  ])
}

/**
 * 適用済みmigrationを読み、未知keyとchecksum変更を検出する。
 * @param params - database: 接続先。
 * @returns 適用済みmigration key集合。
 */
const getAppliedMigrationKeys = async ({
  database,
}: {
  readonly database: DatabaseWithTransaction['database']
}): Promise<ReadonlySet<string>> => {
  const rows = migrationRowsSchema.parse(
    await database.values(
      sql`SELECT \`key\`, \`checksum\` FROM ${sql.raw(`\`${MIGRATION_TABLE_NAME}\``)};`,
    ),
  )
  const canonicalChecksums: ReadonlyMap<string, string> = new Map(
    portfolioMigrations.map((migration) => [migration.key, migration.checksum]),
  )
  const appliedKeys = new Set<string>()
  for (const [key, checksum] of rows) {
    const canonicalChecksum = canonicalChecksums.get(key)
    if (canonicalChecksum === undefined) {
      throw new CorruptedDatabaseFileException(
        `unknown portfolio migration key: ${key}`,
      )
    }
    if (canonicalChecksum !== checksum) {
      throw new CorruptedDatabaseFileException(
        `portfolio migration checksum mismatch: ${key}`,
      )
    }
    appliedKeys.add(key)
  }
  return appliedKeys
}

/**
 * 1件のcanonical migrationを実行して履歴を記録する。
 * @param params - database: 接続先、migration: 実行対象migration。
 * @returns migration完了時に解決するPromise。
 */
const applyMigration = async ({
  database,
  migration,
}: {
  readonly database: DatabaseWithTransaction
  readonly migration: PortfolioMigration
}): Promise<void> => {
  const statements = migration.statements.map((statement) => sql.raw(statement))
  const insertMigrationRecord = createInsertMigrationStatement({ migration })
  if (migration.transactional) {
    await database.transaction([
      ...statements.map((statement) =>
        createMigrationTransactionStep({ statement }),
      ),
      createMigrationTransactionStep({ statement: insertMigrationRecord }),
    ])
    return
  }

  await database.database.run(sql`PRAGMA foreign_keys=OFF;`)
  try {
    // migration適用前の違反件数を基準にし、増加分だけをmigrationのバグとして扱う
    const baseline = await readForeignKeyViolationCounts({
      database: database.database,
    })
    const transactionalStatements = migration.statements
      .filter((statement) => !isForeignKeysToggleStatement({ statement }))
      .map((statement) => sql.raw(statement))
    await database.transaction([
      ...transactionalStatements.map((statement) =>
        createMigrationTransactionStep({ statement }),
      ),
      createForeignKeyRegressionCheckTransactionStep({ migration, baseline }),
      createMigrationTransactionStep({ statement: insertMigrationRecord }),
    ])
  } finally {
    await database.database.run(sql`PRAGMA foreign_keys=ON;`)
  }
}

/**
 * portfolio DBへ未適用のcanonical migrationを順番に適用する。
 * @param params - browser DBへのread/write callbackと新規作成flag。
 * @returns 全migration完了時に解決するPromise。
 */
export const migration = async (params: {
  readonly isInitialMigration?: boolean
  readonly readFromDatabase: RunToDatabase
  readonly writeToDatabase: RunToDatabase
}): Promise<void> => {
  const {
    isInitialMigration = false,
    readFromDatabase,
    writeToDatabase,
  } = params
  await writeToDatabase(async (database) => {
    await initializeMigrationTable({ database, isInitialMigration })
  })
  const appliedMigrationKeys = await readFromDatabase(
    async (database) =>
      await getAppliedMigrationKeys({ database: database.database }),
  )

  for (const canonicalMigration of portfolioMigrations) {
    if (appliedMigrationKeys.has(canonicalMigration.key)) {
      continue
    }
    await writeToDatabase(async (database) => {
      await applyMigration({ database, migration: canonicalMigration })
    })
  }

  // 全migration適用後にDB全体を検査し、既存のCASCADE違反を掃除する。
  // migration中の検査は差分判定のため、既存違反はここでしか解消されない。
  await reportForeignKeyRepair({
    result: await repairForeignKeyViolations({
      readFromDatabase,
      writeToDatabase,
    }),
  })
}
